import { Injectable, inject, LOCALE_ID } from '@angular/core';
import { environment } from '../../environments/environment';
import { NativeDateAdapter } from '@angular/material/core';

@Injectable()
export class CustomDateAdapter extends NativeDateAdapter {
    private static readonly DEFAULT_LOCALE = environment.DEFAULT_LOCALE;
    private readonly appLocale: string = inject(LOCALE_ID);

    // Get first day of the week for current locale
    // Returns 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    getFirstDayOfWeek(): number {
        const locale = this.getEffectiveLocale();

        // Try modern Intl API first
        const intlFirstDay = this.getFirstDayFromIntl(locale);
        if (intlFirstDay != null) {
            return intlFirstDay;
        }

        // Fallback heuristic by region
        return this.fallbackFirstDay(locale);
    }

    private getEffectiveLocale(): string {
        // Prefer the app's Angular locale, then the browser, then configured default
        return this.appLocale || (typeof navigator !== 'undefined' && navigator.language) || CustomDateAdapter.DEFAULT_LOCALE || 'en';
    }

    private getFirstDayFromIntl(locale: string): number | null {
        try {
            const IntlLocale: any = (Intl as any).Locale;
            if (typeof IntlLocale === 'function') {
                const info = new IntlLocale(locale).weekInfo;
                if (info && typeof info.firstDay === 'number') {
                    // Intl weekInfo.firstDay: 1=Monday ... 7=Sunday
                    // NativeDateAdapter expects: 0=Sunday ... 6=Saturday
                    return info.firstDay % 7; // 7->0 (Sunday), 1->1 (Monday), etc.
                }
            }
        } catch {
            // ignore and fall back
        }
        return null;
    }

    private fallbackFirstDay(locale: string): number {
        // Simple region-based fallback. Default to ISO-8601 Monday.
        const region = (locale.split('-')[1] || '').toUpperCase();
        const sunday = new Set(['US', 'CA', 'MX', 'PH', 'TH', 'CO', 'JP']);
        const saturday = new Set(['AE', 'AF', 'IR', 'IQ', 'OM', 'SA']);

        if (region && saturday.has(region)) return 6; // Saturday
        if (region && sunday.has(region)) return 0; // Sunday
        return 1; // Monday (ISO-8601)
    }
}
